import { useMemo } from "react";
import AccessListSection from "admin/components/AccessListSection";
import { AccessDefinition } from "admin/types/interfaces/access";
import { FilesToolIcon } from "~/assets/icons";

const fileSorter = (fileA: AccessDefinition, fileB: AccessDefinition) => {
  // Sort by access first
  if (fileA.access === "write" && fileB.access === "read") {
    return -1;
  } else if (fileA.access === "read" && fileB.access === "write") {
    return 1;
  }

  const fileADepth = fileA.component.definition.split("/").length;
  const fileBDepth = fileB.component.definition.split("/").length;

  // If access is the same, sort by depth of prefix
  if (fileADepth < fileBDepth) {
    return -1;
  } else if (fileADepth > fileBDepth) {
    return 1;
  }

  // If prefix depth is the same, sort by full prefix name
  if (fileA.component.definition < fileB.component.definition) {
    return -1;
  } else if (fileA.component.definition > fileB.component.definition) {
    return 1;
  }

  return 0;
};

const FileAccessList = ({ data }: { data: AccessDefinition[]; hideHeader?: boolean }) => {
  const files = useMemo(() => {
    if (data.length) {
      return data
        .sort(fileSorter)
        .reduce<Record<string, { label: string; access: AccessDefinition["access"] }[]>>(
          (acc, { access, component }) => {
            const [bucketName, ...prefixParts] = component.definition
              .replace("s3://", "")
              .split("/");
            const prefix = prefixParts.join("/");

            acc[bucketName] = acc[bucketName] ?? [];

            // Find existing entry in the accumulator
            const existingEntry = acc[bucketName]?.find((entry) => entry.label === prefix);

            if (existingEntry) {
              // If existing entry has "read" access and current access is "write", upgrade it
              if (existingEntry.access === "read" && access === "write") {
                existingEntry.access = "write";
              }
            } else {
              // Otherwise, add the new entry
              acc[bucketName].push({ label: prefix, access });
            }

            return acc;
          },
          {}
        );
    }

    return null;
  }, [data]);

  if (!files) {
    return null;
  }

  return (
    <div className="space-y-1">
      {Object.entries(files).map(([bucket, prefixes]) => (
        <AccessListSection
          key={bucket}
          defaultOpen
          icon={FilesToolIcon}
          title={bucket}
          items={prefixes}
        />
      ))}
    </div>
  );
};

export default FileAccessList;
